import { isScheduleBalanceAdjustmentEntry } from "@common/helpers/domains/accounting/scheduleLifecycleEntries";
import { summarizeSchedulePeriods } from "@common/helpers/domains/accounting/schedulePeriodsSummary";
import { roundToCents } from "@common/helpers/monetary";
import { ScheduleApplicationListItem } from "@common/schemas/domains/accounting/scheduleApplicationList.schema";
import { ScheduleApplicationStatus } from "@common/types/domains/accounting/schedule";

/**
 * Entries that move the holding balance outside the ladder: a change's
 * catch-up, a stop settled by recognizing or reclassifying the rest, and the
 * reversal that later cancels either. The period ladder alone would miss them.
 */
function sumBalanceAdjustments(options: {
    application: ScheduleApplicationListItem;
    ladderPeriodIds: ReadonlySet<string>;
    asOfDate: string;
}): number {
    const { ladderPeriodIds } = options;
    return options.application.lifecycleJournalEntries.reduce(
        (sum, entry) =>
            isScheduleBalanceAdjustmentEntry({ entry, ladderPeriodIds }) &&
            (entry.dateOnly === null || entry.dateOnly <= options.asOfDate)
                ? sum + entry.amount
                : sum,
        0,
    );
}

/**
 * "Remaining balance" follows the Schedule column card: the applied amount less
 * every recognition period dated on or before `asOfDate`, regardless of
 * posting. The API's `remainingAmount` is posted-based and stays on the item
 * for the lifecycle flows that read it.
 */
export function computeRemainingBalance(options: {
    application: ScheduleApplicationListItem;
    ladderPeriodIds: ReadonlySet<string>;
    asOfDate: string;
}): number {
    const { application, ladderPeriodIds, asOfDate } = options;
    if (application.status === ScheduleApplicationStatus.unwound) {
        return 0;
    }
    return roundToCents(
        summarizeSchedulePeriods({
            amount: application.amount,
            periods: application.periods,
            asOfDate,
        }).remainingAmount -
            sumBalanceAdjustments({ application, ladderPeriodIds, asOfDate }),
    );
}
